import type { FC } from 'react'

import { ExternalLink } from '@skytech/ui'
import AlertDecagramIcon from 'mdi-react/AlertDecagramIcon'
import CheckDecagramIcon from 'mdi-react/CheckDecagramIcon'

import { Translation } from '@/app/translations/Translation'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { LocalDate } from '@/components/LocalDate'
import { LocalDateTime } from '@/components/LocalDateTime'

import type { PolicyOrderExtended } from '../types'

const EcomPolicyOrderDetails: FC<{ order: PolicyOrderExtended }> = ({ order }) => {
  const {
    downPaymentRemaining,
    products,
    downPayments = 0,
    coverAmount = 0,
    orderDate,
    deliveryDate,
    remainderAmount,
    vendorSalesOrderNo,
    despatchTrackingId,
    currency,
    approvalLog,
  } = order
  const totalCost = products.reduce((acc, cur) => acc + cur.price * cur.quantity, 0)
  const approval = approvalLog.length > 0 ? approvalLog[approvalLog.length - 1] : null
  // Tracking link should be disabled for non-norwegian orders
  const isNorwegianTrackingLink = currency === 'NOK'
  return (
    <section>
      <h3>
        <Translation id="Order" />
      </h3>
      <DataPoints>
        <DataPoint header={<Translation id="Order number" />} value={vendorSalesOrderNo || '—'} />
        <DataPoint
          header={<Translation id="Cost total" />}
          value={<FormattedNumberWithCurrency amount={totalCost} currency={currency} />}
        />
        <DataPoint
          header={<Translation id="Cover amount" />}
          value={<FormattedNumberWithCurrency amount={coverAmount} currency={currency} />}
        />
        <DataPoint
          header={<Translation id="Remainder amount" />}
          value={<FormattedNumberWithCurrency amount={remainderAmount} currency={currency} />}
        />
        <DataPoint
          header={<Translation id="Down payments" />}
          value={
            downPayments > 0 ? (
              <>
                {downPayments} x{' '}
                <FormattedNumberWithCurrency
                  amount={remainderAmount / downPayments}
                  currency={currency}
                />
              </>
            ) : (
              '0'
            )
          }
        />
        <DataPoint
          header={<Translation id="Down payment remaining" />}
          value={
            <FormattedNumberWithCurrency amount={downPaymentRemaining || 0} currency={currency} />
          }
        />
        <DataPoint
          header={<Translation id="Order date" />}
          value={<LocalDate date={orderDate} />}
        />
        <DataPoint
          header={<Translation id="Delivery date" />}
          value={deliveryDate ? <LocalDate date={orderDate} /> : '-'}
        />
        {despatchTrackingId && (
          <DataPoint
            header={<Translation id="Tracking" />}
            values={despatchTrackingId.split('|').map(trackingId => {
              let trackingUrl = ''
              if (isNorwegianTrackingLink)
                trackingUrl = `https://sporing.posten.no/sporing/${trackingId}`
              if (trackingId.startsWith('http')) trackingUrl = trackingId

              if (trackingUrl)
                return (
                  <div key={trackingId}>
                    <ExternalLink href={trackingUrl} target="_blank" rel="noopener noreferrer">
                      {trackingId}
                    </ExternalLink>
                  </div>
                )

              return <div key={trackingId}>{trackingId}</div>
            })}
          />
        )}
        {approval?.isApproved && (
          <>
            <DataPoint
              header={<Translation id="Approval date" />}
              value={
                <>
                  <LocalDateTime date={approval.datetime} />
                  {/* todo: Remove ! when refactored */}
                  <CheckDecagramIcon className="ml-1 !h-6 !w-6 fill-green-500" />
                </>
              }
            />
            <DataPoint header={<Translation id="Approved by" />} value={approval.userName} />
          </>
        )}

        {approval?.isRejected && (
          <>
            <DataPoint
              header={<Translation id="Rejection date" />}
              value={
                <>
                  <LocalDateTime date={approval.datetime} />
                  {/* todo: Remove ! when refactored */}
                  <AlertDecagramIcon className="ml-1 !h-6 !w-6 fill-red-500" />
                </>
              }
            />
            <DataPoint header={<Translation id="Rejected by" />} value={approval.userName} />
          </>
        )}
      </DataPoints>
    </section>
  )
}

export default EcomPolicyOrderDetails
